/**
 * Com que nome o browser salva o que a API devolveu (set/2026).
 *
 * Quem manda é o `Content-Disposition`: só o servidor sabe o nome do original.
 * O que este módulo resolve é o **segundo** nome — o de reserva —, porque o
 * primeiro pode não chegar: quando a chamada é de outra origem o navegador
 * entrega o corpo e esconde os cabeçalhos não safelisted, e aí
 * `res.headers.get("content-disposition")` é `null` mesmo com o servidor
 * mandando tudo certo (o backend expõe o cabeçalho desde o `CORS_EXPOSE_HEADERS`,
 * mas um proxy pela frente ainda pode comê-lo).
 *
 * A reserva boa é o nome que a tela **já tem na mão** — a listagem do
 * gerenciador traz `filename` em cada arquivo. A reserva ruim era uma constante
 * `"arquivo.pdf"`: além de perder o nome, mentia a extensão, e um JPEG era
 * baixado como PDF.
 *
 * Lógica fora do `api.ts` pelo motivo de sempre: `node --test` é o único
 * caminho de teste do projeto, e `api.ts` depende de `import.meta.env`.
 */

/** Um nome de arquivo serve como nome de download? */
function nomeUtil(valor: string | null | undefined): valor is string {
  if (!valor) return false;
  const limpo = valor.trim();
  // `/` e `\` sairiam do diretório de downloads; o browser sanitiza, mas o
  // nome fica irreconhecível. Nome com barra não é nome: é caminho.
  return limpo.length > 0 && !limpo.includes("/") && !limpo.includes("\\");
}

/**
 * O nome declarado no `Content-Disposition`, ou `null` quando não há.
 *
 * Lê as duas formas: `filename*=UTF-8''…` (RFC 5987, a que o backend manda —
 * é a que preserva acento) e `filename="…"` puro, de quem estiver na frente.
 */
export function nomeDoContentDisposition(disposition: string | null | undefined): string | null {
  if (!disposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      const decodificado = decodeURIComponent(utf8[1]);
      if (nomeUtil(decodificado)) return decodificado.trim();
    } catch {
      // Percentagem malformada: cai no `filename=` puro, e depois na reserva.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain && nomeUtil(plain[1])) return plain[1].trim();
  return null;
}

/**
 * `arte.pdf` com `"jpg"` vira `arte.jpg` — a reserva de um export.
 *
 * O nome bom do export sai do servidor (`_export_filename`, que ainda numera a
 * página quando há mais de uma); aqui é só o que a tela consegue montar sozinha
 * quando o cabeçalho não chega, e o que importa é não prometer PDF entregando
 * JPEG. Sem nome de origem devolve vazio: quem chama decide o último recurso.
 */
export function comExtensao(filename: string | null | undefined, ext: string): string {
  if (!nomeUtil(filename)) return "";
  const limpo = filename.trim();
  const stem = limpo.includes(".") ? limpo.slice(0, limpo.lastIndexOf(".")) : limpo;
  return `${stem.trim() || limpo}.${ext}`;
}

/**
 * O nome final: o do servidor quando ele chega, o da tela quando não chega.
 *
 * `fallback` é o último recurso e nunca fica vazio — sem nome nenhum o browser
 * salvaria o UUID do blob.
 */
export function nomeDoDownload(
  disposition: string | null | undefined,
  fallback: string | null | undefined,
  ultimoRecurso = "download",
): string {
  return nomeDoContentDisposition(disposition) ?? (nomeUtil(fallback) ? fallback.trim() : ultimoRecurso);
}
